import React, { useState, JSX } from "react";
import _compact from "lodash/compact";
import {
  FacetType,
  IFacet,
  useLaunchDarklyFlags,
  FEATURE_FLAGS,
} from "shared-services";
import { useTranslation } from "i18n";
import { IFilterTagProps } from "../../Atoms/FilterTag/FilterTag";
import { FilterControl } from "../FilterControl/FilterControl";
import {
  FilterDrawer,
  IFilterDrawerItem,
} from "../FilterControl/FilterDrawer/FilterDrawer";
import {
  FilterConfig,
  FiltersFieldNames,
  IFilterStats,
  IFilterValues,
  SetFilterValues,
} from "./types";
import "./index.scss";

interface Props<T> {
  values: T;
  defaultValues: T;
  filterDrawerValues: T;
  setFilterValues: SetFilterValues<T>;
  setFilterDrawerValues: SetFilterValues<T>;
  facets: Record<FacetType, IFacet[]>;
  filterData: Record<FiltersFieldNames, unknown>;
  stats?: IFilterStats;
  config: FilterConfig<T>;
}

function Filters<T extends IFilterValues>({
  values,
  defaultValues,
  filterDrawerValues,
  setFilterValues,
  setFilterDrawerValues,
  facets,
  stats,
  config,
  filterData,
}: Readonly<Props<T>>) {
  const flags = useLaunchDarklyFlags([FEATURE_FLAGS.YPsPlayingAgeMin]);
  const { t } = useTranslation();
  const [isVisibleFilterDrawer, setIsVisibleFilterDrawer] = useState(false);

  const closeFilterDrawer = () => setIsVisibleFilterDrawer(false);

  const { tags, filterDrawerConfig, filterControlComponents } = config.reduce<{
    tags: IFilterTagProps[];
    filterDrawerConfig: IFilterDrawerItem[];
    filterControlComponents: JSX.Element[];
  }>(
    (acc, { getFilterFiledProps, name, facetNames, modifiers }) => {
      const result = getFilterFiledProps({
        name,
        facetNames,
        values,
        filterDrawerValues,
        setFilterValues,
        setFilterDrawerValues,
        facets,
        filterData,
        stats,
        modifiers,
        t,
        flags,
      });
      const {
        tags = [],
        filterDrawerConfig,
        filterControlComponent,
      } = result || {};

      return {
        tags: _compact([...acc.tags, ...tags]),
        filterDrawerConfig: _compact([
          ...acc.filterDrawerConfig,
          filterDrawerConfig,
        ]),
        filterControlComponents: _compact([
          ...acc.filterControlComponents,
          filterControlComponent,
        ]),
      };
    },
    {
      tags: [],
      filterDrawerConfig: [],
      filterControlComponents: [],
    }
  );

  return (
    <div className="bg-color-neutral-three">
      <FilterControl
        onViewAllFilters={() => setIsVisibleFilterDrawer(true)}
        onClearAllFilters={() => {
          setFilterValues(defaultValues);
          closeFilterDrawer();
        }}
        tags={tags}
        items={filterControlComponents}
        isControlledTags
      />
      {isVisibleFilterDrawer && (
        <FilterDrawer
          className="g-bg-default"
          filters={filterDrawerConfig}
          onClose={closeFilterDrawer}
          onClearAllFilters={() => {
            setFilterValues(defaultValues);
            closeFilterDrawer();
          }}
          onApplyFilters={() => {
            setFilterValues(filterDrawerValues);
            closeFilterDrawer();
          }}
        />
      )}
    </div>
  );
}

export default Filters;
